import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Megaphone, CheckCircle, Users, TrendingUp, BarChart, Target } from 'lucide-react';

const MarketingManagers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What metrics should marketing managers track for performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Track pipeline influenced, MQL/SQL generation, campaign ROI, conversion rates, brand awareness metrics, and customer acquisition cost (CAC). Also document content performance, channel growth, and revenue attribution. Example: 'Generated 2,400 MQLs through campaigns with 23% SQL conversion rate, contributing to $1.2M influenced pipeline.'"
        }
      },
      {
        "@type": "Question",
        "name": "How do marketing managers demonstrate ROI in performance reviews?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Connect marketing activities to revenue outcomes. Track influenced pipeline, cost per lead, campaign ROI, and revenue attribution. Use multi-touch attribution when possible. Show both efficiency (lower CAC) and effectiveness (higher conversion rates)."
        }
      },
      {
        "@type": "Question",
        "name": "What achievements beyond lead generation should marketers document?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Document brand building work, thought leadership content, sales enablement materials, process improvements, technology implementations, and cross-functional collaborations. These strategic contributions often have longer-term impact than immediate lead numbers."
        }
      },
      {
        "@type": "Question",
        "name": "How often should marketing managers track their accomplishments?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Track weekly, ideally after every campaign launch or significant milestone. Document campaign results, content performance, and strategic initiatives as they happen. Use a tool like BragBox to capture wins in real-time so review preparation becomes effortless."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Performance Review Tips for Marketing Managers | BragBox</title>
        <meta name="description" content="Ace your marketing performance review with actionable tips. Learn how to showcase campaign ROI, pipeline influence, and strategic brand-building achievements." />
        <meta name="keywords" content="marketing manager performance review, marketing self-evaluation, campaign ROI, marketing metrics, marketing career growth, demand generation review" />
        <link rel="canonical" href="https://bragbox.ai/tips/marketing-managers" />
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-[#1F2A44] via-[#2d3a5c] to-[#1F2A44]">
        {/* Header */}
        <header className="bg-[#1F2A44]/90 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                <span className="text-[#1F2A44] font-bold text-sm">B</span>
              </div>
              <span className="text-white font-semibold text-lg">BragBox</span>
            </Link>
            <Link
              to="/login?signup=true"
              className="bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Start free
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#C4A35A]/20 text-[#C4A35A] px-4 py-2 rounded-full text-sm mb-6">
              <Megaphone className="w-4 h-4" />
              Marketing Career Tips
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Performance Review Tips for Marketing Managers
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Your campaigns drive pipeline, but attribution is messy. Learn how to document marketing impact, prove ROI, and showcase the strategic work that often goes unrecognized.
            </p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-12 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">Why Marketers Struggle with Performance Reviews</h2>
            <p className="text-gray-300 mb-6">
              You launched a webinar series that filled the top of funnel. You created sales enablement materials that helped close a six-figure deal. You repositioned the brand messaging and saw organic traffic double. But when review time comes, leadership asks "What's your lead number?"
            </p>
            <p className="text-gray-300">
              Marketing attribution is inherently complex. The content that influenced a deal might have been consumed six months before the sales conversation. The brand work that makes outbound more effective doesn't show up in last-touch attribution. Your real impact is often invisible in simple metrics.
            </p>
          </div>
        </section>

        {/* Tips Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">10 Actionable Tips for Your Marketing Review</h2>
            
            <div className="space-y-8">
              {/* Tip 1 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">1. Connect Activities to Pipeline</h3>
                    <p className="text-gray-300 mb-3">
                      Don't just report MQLs. Show influenced pipeline, opportunities created, and revenue attribution wherever possible.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Generated 3,200 MQLs with 28% SQL conversion rate. Marketing-influenced pipeline reached $4.8M, with $1.2M closed-won directly attributed to campaign touches within 90 days of purchase."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 2 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">2. Document Campaign ROI</h3>
                    <p className="text-gray-300 mb-3">
                      Calculate return on individual campaigns. Show cost per lead, cost per opportunity, and ultimate ROI where attribution allows.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Q3 paid campaign achieved 340% ROI: $45K spend generated $198K influenced pipeline with 22% close rate. Cost per opportunity dropped from $890 to $620 through landing page optimization."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 3 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">3. Track Content Performance</h3>
                    <p className="text-gray-300 mb-3">
                      Content is the fuel of modern marketing. Document views, engagement, lead generation, and sales use of key content pieces.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Published 24 blog posts generating 180K organic visits and 890 MQLs. Flagship guide downloaded 2,400 times with 15% MQL conversion. Three pieces ranked page 1 for target keywords."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 4 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">4. Highlight Sales Enablement Impact</h3>
                    <p className="text-gray-300 mb-3">
                      Materials that help sales close deals have direct revenue impact. Track usage, sales feedback, and deals where enablement content played a role.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Created 8 sales enablement pieces with 78% adoption rate across sales team. ROI calculator used in 45 deals, contributing to 34% higher win rate on calculator-influenced opportunities."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 5 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#C4A35A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">5. Document Brand Building Work</h3>
                    <p className="text-gray-300 mb-3">
                      Brand awareness drives long-term demand. Track share of voice, brand search trends, PR wins, and thought leadership placement.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Increased branded search volume 67% YoY. Secured coverage in 12 industry publications reaching 450K combined audience. Company mentioned in 3 analyst reports as emerging vendor."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 6 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">6</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">6. Track Channel Growth</h3>
                    <p className="text-gray-300 mb-3">
                      Growing owned channels creates compounding value. Document subscriber growth, social following, community size, and engagement trends.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Grew email list from 8K to 22K subscribers (175% increase) with 32% open rate. LinkedIn following increased 89% with engagement rate 3x industry average. Podcast launched, reaching 5K monthly downloads."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 7 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">7</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">7. Document Process Improvements</h3>
                    <p className="text-gray-300 mb-3">
                      Technology implementations, automation, and workflow improvements show operational excellence. Track efficiency gains and cost savings.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Implemented marketing automation reducing manual work by 12 hours/week. Built lead scoring model improving MQL-to-SQL conversion 34%. Created reporting dashboard eliminating 6 hours of monthly manual reporting."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 8 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">8</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">8. Capture Event Marketing Success</h3>
                    <p className="text-gray-300 mb-3">
                      Events drive pipeline and relationships. Track attendance, leads generated, opportunities influenced, and deal acceleration.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Executed 4 webinars with 2,800 registrants and 52% attendance rate, generating 340 MQLs. Managed trade show presence resulting in 89 qualified conversations and $780K influenced pipeline."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 9 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">9</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">9. Show Budget Management</h3>
                    <p className="text-gray-300 mb-3">
                      Responsible budget management shows business acumen. Track budget adherence, cost optimizations, and reallocation decisions that improved results.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Example Achievement:</p>
                      <p className="text-gray-300 text-sm">"Managed $420K annual budget, coming in 8% under while exceeding lead targets by 23%. Identified $35K in inefficient spend, reallocated to high-performing channels yielding 2.4x better cost per lead."</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tip 10 */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-[#C4A35A]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-[#C4A35A] font-bold">10</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">10. Document Wins as They Happen</h3>
                    <p className="text-gray-300 mb-3">
                      Marketing moves fast. Campaign results, content wins, and strategic achievements should be captured in real-time.
                    </p>
                    <div className="bg-[#1F2A44]/50 rounded-lg p-4">
                      <p className="text-sm text-[#C4A35A] font-medium mb-1">Pro Tip:</p>
                      <p className="text-gray-300 text-sm">After every campaign wrap-up, content publish, or notable metric hit, take 30 seconds to log it in BragBox. Include the numbers while they're fresh. Your review prep will thank you.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Metrics Examples Section */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Key Metrics Marketing Managers Should Track</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Demand Generation</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• MQL/SQL volume and conversion</li>
                  <li>• Pipeline influenced</li>
                  <li>• Cost per lead / cost per opp</li>
                  <li>• Campaign ROI</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Content & SEO</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Organic traffic growth</li>
                  <li>• Keyword rankings</li>
                  <li>• Content engagement rates</li>
                  <li>• Lead attribution by content</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Brand & Awareness</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Branded search volume</li>
                  <li>• Share of voice</li>
                  <li>• Press/media coverage</li>
                  <li>• Social following & engagement</li>
                </ul>
              </div>
              <div className="bg-white/10 rounded-xl p-6">
                <h3 className="text-[#C4A35A] font-semibold mb-3">Operations</h3>
                <ul className="text-gray-300 space-y-2">
                  <li>• Budget adherence</li>
                  <li>• Process efficiency gains</li>
                  <li>• Tech stack optimization</li>
                  <li>• Team productivity metrics</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What metrics should marketing managers track for performance reviews?</h3>
                <p className="text-gray-300">Track pipeline influenced, MQL/SQL generation, campaign ROI, conversion rates, brand awareness metrics, and customer acquisition cost (CAC). Also document content performance, channel growth, and revenue attribution. Example: "Generated 2,400 MQLs through campaigns with 23% SQL conversion rate, contributing to $1.2M influenced pipeline."</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How do marketing managers demonstrate ROI in performance reviews?</h3>
                <p className="text-gray-300">Connect marketing activities to revenue outcomes. Track influenced pipeline, cost per lead, campaign ROI, and revenue attribution. Use multi-touch attribution when possible. Show both efficiency (lower CAC) and effectiveness (higher conversion rates).</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">What achievements beyond lead generation should marketers document?</h3>
                <p className="text-gray-300">Document brand building work, thought leadership content, sales enablement materials, process improvements, technology implementations, and cross-functional collaborations. These strategic contributions often have longer-term impact than immediate lead numbers.</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-2">How often should marketing managers track their accomplishments?</h3>
                <p className="text-gray-300">Track weekly, ideally after every campaign launch or significant milestone. Document campaign results, content performance, and strategic initiatives as they happen. Use a tool like BragBox to capture wins in real-time so review preparation becomes effortless.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-[#C4A35A]/20 to-[#C4A35A]/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Prove Your Marketing Impact</h2>
            <p className="text-xl text-gray-300 mb-8">
              BragBox helps marketing managers capture campaign wins, content performance, and strategic achievements throughout the year.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/login?signup=true"
                className="inline-block bg-[#C4A35A] hover:bg-[#d4b36a] text-[#1F2A44] px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
              >
                Start free
              </Link>
              <Link to="/login" className="text-gray-300 underline underline-offset-4 hover:text-white transition-colors">
                Already have an account? Log in.
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-gradient-to-br from-[#C4A35A] to-[#d4b36a] rounded-lg flex items-center justify-center">
                  <span className="text-[#1F2A44] font-bold text-sm">B</span>
                </div>
                <span className="text-white font-semibold">BragBox</span>
              </Link>
              <div className="flex gap-6 text-gray-400 text-sm">
                <Link to="/" className="hover:text-white transition-colors">Home</Link>
                <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Login</Link>
              </div>
              <p className="text-gray-500 text-sm">© 2026 BragBox. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default MarketingManagers;
